/**
 * „Niesprawdzone lokale" (właściciel 2026-10-02, docx: „przycisk zestawiający niesprawdzone jak
 * w tablecie oraz możliwość eksportu / udostępnienia większej ilości budynków") — czyste helpery
 * modala: grupowanie, tekst do schowka (np. do maila dla zarządcy) i CSV dla Excela.
 * Dane: POST /buildings/unchecked-apartments. Etykiety statusów wyłącznie z lustra STATUS_LABELS.
 */
import type { UncheckedApartment, UncheckedApartmentsResponse } from '../services/api';
import { STATUS_LABELS } from '../ds/StatusMap';
import { excelCsv } from './download';
// E3 (migr. 071, review R13): oznaczenie na drzwiach obok numeru CEEB — zarządca zna lokale po drzwiach.
import { apartmentDoorMarking, apartmentNumberWithMarking } from './apartmentLabels';

/** Lokal bez żadnego wyniku w bieżącym przeglądzie — ta sama etykieta, co na liście lokali (kanon). */
export const NOT_VISITED_LABEL = STATUS_LABELS.not_scheduled;

/**
 * Nagłówki grup w oknie i w tekście do schowka (E13/X10, 2026-10-20): „Nieobecność" i „Odmowa" to
 * etykiety statusów z kanonu (razem = „Nieudostępnione", jak w PDF podsumowania i na tablecie),
 * „Niesprawdzone" = liczba mnoga etykiety lokalu bez wyniku („Niesprawdzony").
 */
export const UNCHECKED_GROUP_LABELS = {
  brak_dostepu: STATUS_LABELS.brak_dostepu,
  odmowa_lokatora: STATUS_LABELS.odmowa_lokatora,
  notVisited: 'Niesprawdzone',
} as const;

/** Kolejność grup: nieobecności, odmowy, niesprawdzone. */
export const UNCHECKED_GROUP_ORDER = ['brak_dostepu', 'odmowa_lokatora', 'notVisited'] as const;

export function uncheckedStatusLabel(status: UncheckedApartment['status']): string {
  return status ? STATUS_LABELS[status] : NOT_VISITED_LABEL;
}

export interface UncheckedGroups {
  brak_dostepu: UncheckedApartment[];
  odmowa_lokatora: UncheckedApartment[];
  notVisited: UncheckedApartment[];
}

/** Nieobecności osobno (owner: „i te z nieobecnością osobno"), potem odmowy i nieodwiedzone. */
export function groupUnchecked(apartments: UncheckedApartment[]): UncheckedGroups {
  return {
    brak_dostepu: apartments.filter((a) => a.status === 'brak_dostepu'),
    odmowa_lokatora: apartments.filter((a) => a.status === 'odmowa_lokatora'),
    notVisited: apartments.filter((a) => !a.status),
  };
}

const plDay = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}.${m}.${y}` : iso;
};

export function attemptsText(a: UncheckedApartment): string {
  return a.attemptDates.map(plDay).join(', ');
}

export function totalUnchecked(data: UncheckedApartmentsResponse): number {
  return data.buildings.reduce((n, b) => n + b.apartments.length, 0);
}

/**
 * Tekst do schowka — wklejany do maila/SMS-a dla zarządcy. Budynek po budynku, lokale po grupach;
 * budynki bez niesprawdzonych lokali są pomijane (nie ma czego zgłaszać).
 */
export function uncheckedText(data: UncheckedApartmentsResponse): string {
  const blocks = data.buildings
    .filter((b) => b.apartments.length > 0)
    .map((b) => {
      const g = groupUnchecked(b.apartments);
      const lines = [`${b.address} — niesprawdzone lokale: ${b.apartments.length} z ${b.apartmentsTotal}`];
      for (const key of UNCHECKED_GROUP_ORDER) {
        const list = g[key];
        if (list.length) {
          lines.push(`${UNCHECKED_GROUP_LABELS[key]}: ${list.map((a) => apartmentNumberWithMarking(a.apartmentNumber, a)).join(', ')}`);
        }
      }
      return lines.join('\n');
    });
  return blocks.join('\n\n');
}

/**
 * CSV dla Excela w polskich ustawieniach: separator `;`, BOM, CRLF. Wiersz = lokal. „Lokal" = numer CEEB
 * (sortowalny w Excelu), oznaczenie na drzwiach (E3) w osobnej kolumnie „Klatka / drzwi".
 */
export function uncheckedCsv(data: UncheckedApartmentsResponse): string {
  const lines: (string | number)[][] = [['Budynek', 'Lokal', 'Klatka / drzwi', 'Status', 'Próby (daty)']];
  for (const b of data.buildings) {
    for (const a of b.apartments) {
      lines.push([b.address, a.apartmentNumber, apartmentDoorMarking(a), uncheckedStatusLabel(a.status), attemptsText(a)]);
    }
  }
  return excelCsv(lines);
}
